import { FC, useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Button, TextArea, Badge, Tooltip, useToast, Spinner, Timeline } from '@aidkitorg/component-library';
import { usePost } from '../../../../API';
import { useLocalizedStrings } from '../../../../Localization';

type Activity = {
  uid: string;
  kind: 'Note' | 'Support Action' | 'Applicant Note';
  action: string;
  created_at: number;
  created_by: string;
};

export interface CaseNotesTabProps {
  contact: string;
  applicant?: string;
  channel?: string;
  updateActivity?: (activity: any[]) => void;
}

export const CaseNotesTab: FC<CaseNotesTabProps> = ({
  contact,
  applicant,
  channel,
  updateActivity,
}) => {
  const [note, setNote] = useState('');
  const createNoteAPI = usePost('/support/create_note');
  const getContactActivityAPI = usePost('/support/get_activity');
  const getNotesAPI = usePost('/applicant/get_notes');
  const L = useLocalizedStrings();
  const { toast } = useToast();
  const queryClient = useQueryClient();

  // Fetch activity with tanstack query
  const { data: activity = [], isLoading } = useQuery({
    queryKey: ['caseActivity', contact, applicant, channel],
    queryFn: async (): Promise<Activity[]> => {
      let activity: Activity[] = [];
      const response = await getContactActivityAPI({ contact, channel });
      if (response?.activity) {
        activity = [...response.activity];
        if (updateActivity) {
          updateActivity(response.activity);
        }
      }

      if (applicant) {
        const response = await getNotesAPI({ applicant });
        if (!('error' in response)) {
          const applicantNotesAsContactNotes: Activity[] = (response.data || [])
            .map((note) => ({
              uid: note.id || 'applicant-note',
              kind: 'Applicant Note' as const,
              action: note.message,
              created_at: note.created_at,
              created_by: note.author,
            }))
            .filter((note) => note.created_by.toLowerCase() !== 'system');
          activity = [...activity, ...applicantNotesAsContactNotes].sort(
            (a, b) => new Date(b.created_at).getTime() - new Date(a.created_at).getTime()
          );
        }
      }

      return activity;
    },
    enabled: !!contact,
  });

  // Create note mutation
  const createNoteMutation = useMutation({
    mutationFn: async (noteText: string) => {
      return await createNoteAPI({
        contact,
        note: noteText,
        channel,
      });
    },
    onSuccess: (response) => {
      if (response?.created) {
        toast({
          description: L.support.note_created,
          variant: 'success',
        });
        setNote('');
        queryClient.invalidateQueries({ queryKey: ['caseActivity', contact, applicant, channel] });
      }
    },
    onError: () => {
      toast({
        description: L.support.failed_to_create_note,
        variant: 'error',
      });
    },
  });

  const handlePostNote = () => {
    if (note.trim()) {
      createNoteMutation.mutate(note);
    }
  };

  return (
    <div className="flex flex-col h-full">
      <div className="mb-3">
        <TextArea
          label={L.support.add_your_note}
          value={note}
          onChange={(e) => setNote(e.target.value)}
          placeholder={L.support.add_your_note_dot}
          rows={3}
          className="w-full mb-3"
        />
        <div className="flex justify-end">
          <Button size="sm" onClick={handlePostNote} disabled={!note.trim() || createNoteMutation.isPending}>
            {L.support.post_note}
          </Button>
        </div>
      </div>

      {isLoading ? (
        <div className="flex justify-center items-center flex-1">
          <Spinner size="large" />
        </div>
      ) : (
        <div className="flex-1 overflow-y-auto pb-3">
          <Timeline>
            {activity.map((activityItem, activityItemIdx) => (
              <Timeline.Item
                key={activityItem.uid + "-" + activityItemIdx}
                time={activityItem.created_at}
                variant="primary"
              >
                {activityItem.kind === "Note" || activityItem.kind === "Applicant Note" ? (
                  <div>
                    <div className="flex items-center gap-2 mb-1 text-sm">
                      <div className="text-sm text-gray-500">
                        <span className="font-medium text-gray-900">{activityItem.created_by}</span>{" "}
                        {L.support.commented.toLocaleLowerCase()}
                      </div>
                      {activityItem.kind === "Applicant Note" && (
                        <Tooltip text={L.support.applicant_note_tooltip}>
                          <Badge variant="info" className="text-xs">
                            {L.support.applicant_note}
                          </Badge>
                        </Tooltip>
                      )}
                    </div>
                    <div className="text-sm text-gray-700">{activityItem.action}</div>
                  </div>
                ) : (
                  <div className="text-sm text-gray-500">
                    <span className="font-medium text-gray-900">{activityItem.created_by}</span> {activityItem.action}
                  </div>
                )}
              </Timeline.Item>
            ))}
          </Timeline>
        </div>
      )}
    </div>
  );
};
